import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM, { unstable_batchedUpdates } from 'react-dom';
import ReactGA from 'react-ga';
import { connect } from 'react-redux';
import { useSnackbar } from 'notistack';
import { submit, updateSyncErrors, reset, change } from 'redux-form';
import { useQuery } from '@apollo/react-hooks';

import { ACCOMMODATION } from 'graphql/queries/accommodations';
import { ACTIVITY } from 'graphql/queries/activities';
import {RENTAL_ITEM} from 'graphql/queries/rental-items';
import { USER_SETTINGS_WITHOUT_SECRET_KEYS } from 'graphql/queries/settings';

import ConfirmContext from 'contexts/ConfirmContext';

import {
  WIDGET_ROOT,
  WIDGET_WRAPPER_ID,
  ADD_ONS_INDEX,
  RELATED_PRODUCTS_INDEX,
  MENU,
  MENU_RENTAL,
  ACCOMMODATION_TITLE,
  ACTIVITY_TITLE,
  RENTAL_ITEM_TITLE
} from 'constants/widget';

import {
  CATEGORY_PRICES,
  DAILY_PRICES,
  FIXED_PRICES,
  FUTURE_PRICES,
  ACTIVITY_FUTURE_PRICES
} from 'pages/widget/constants';

import {
  getReceivedAccommodationData
} from 'utils/accommodations-and-activities/prepareReceivedAccommodationData';
import { getReceivedActivityData } from 'utils/accommodations-and-activities/prepareReceivedActivityData';
import {getReceivedRentalItemsData} from 'utils/rental-items/prepareReceivedRentalItemsData';

import { TabPanel } from 'components';

import Header from './components/header/Header';
import Menu from './components/menu/Menu';
import GeneralAccommodationsList from './components/general-accommodations-list/GeneralAccommodationsList';
import GeneralActivitiesList from './components/general-activities-list/GeneralActivitiesList';
import GeneralRentalItemsList from './components/general-rental-items-list/GeneralRentalItemsList';
import ProductInfo from './components/product-info/ProductInfo';
import DateAndGuests from './components/date-and-guests/DateAndGuests';
import DatesAndUnits from "./components/date-and-units/DateAndUnits";
import AddOns from './components/add-ons/AddOns';
import MandatoryFees from './components/mandatory-fees/MandatoryFees';
import Products from 'pages/widget/components/related-products/Products';
import Checkout from './components/checkout/Checkout';
import RentalCheckout from './components/checkout/rentalCheckout';
import Payment from './components/payment/Payment';
import Footer from './components/footer/Footer';

import useStyles from './styles';
import { getRange } from '../../utils/book-accommodation/availableDates';
import { useAccommodationsInfiniteQuery } from './hooks/useAccommodationsInfiniteQuery';
import { useActivitiesInfiniteQuery } from './hooks/useActivitiesInfiniteQuery';
import { useRentalItemsInfiniteQuery } from './hooks/useRentalItemsInfiniteQuery';
import WidgetLoader from './components/widget-loader/WidgetLoader';

const AVAILABLE_CURRENCIES = ['NOK', 'EUR', 'USD'];
const PRODUCT_SECTION_INDEX = 0;
const ADD_ONS_SECTION_INDEX = 1;
const RELATED_PRODUCT_SECTION_INDEX = 2;
const CHECKOUT_SECTION_INDEX = 3;
const PAYMENT_SECTION_INDEX = 4;

const Widget = ({
  submitForm,
  updateFormErrors,
  resetWidgetForm,
  widgetForm,
  ownerId,
  receivedAccommodationId,
  receivedActivityId,
  receivedRentalId,
  languages,
  logo,
  privacyPolicy,
  termsAndConditions,
  changeWidgetFormFieldValue
}) => {
 
  
  const classes = useStyles();
  const [t] = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const [activityDisabledDates, setActivityDisabledDates] = useState([]);

  const [productId, setProductId] = useState(receivedAccommodationId || receivedActivityId || receivedRentalId);
  const [activity, setActivity] = useState(null);
  const [receivedActivity, setReceivedActivity] = useState(null);
  const [rentalItem, setRentalItem] = useState(null);  
  const [receivedRental, setReceivedRental] = useState(null);
  const [accommodation, setAccommodation] = useState(null);
  const [receivedAccommodation, setReceivedAccommodation] = useState(
    null
  );
  const [menuIndex, setMenuIndex] = useState(0);
  const [productsMenuIndex, setProductsMenuIndex] = useState(0);  

  const [lastSubmittedFormIndex, setLastSubmittedFormIndex] = useState(0);
  const [isSubmit, setIsSubmit] = useState(false);
  const [isProductsList, setIsProductsList] = useState(!receivedAccommodationId && !receivedActivityId && !receivedRentalId);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [initialSelectedCurrency, setInitialSelectedCurrency] = useState('NOK');
  const [initialSelectedLanguage, setInitialSelectedLanguage] = useState(languages[0]);
  const [initialAvailableCurrencies, setInitialAvailableCurrencies] = useState(AVAILABLE_CURRENCIES);
  const [disabledSteps, setDisabledStep] = useState([]);

  const [zoomLevel, setZoomLevel] = useState(window.devicePixelRatio);

  const { loading: settingsLoading, data: settingsData } = useQuery(
    USER_SETTINGS_WITHOUT_SECRET_KEYS,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        businessOwnerId: ownerId
      }
    }
  );

  const {
    accommodationsPage,
    accommodations,
    fetchAccommodationsData,
    loading: isAccommodationsLoading,
    perPage: accommodationsPerPage
  } = useAccommodationsInfiniteQuery({
    startDate: widgetForm?.values?.range?.startDate,
    endDate: widgetForm?.values?.range?.endDate,
    guests: widgetForm?.values?.numberOfGuests || '',
    ownerId
  }, !!productId);

  const {
    activitiesPage,
    activities,
    fetchActivitiesData,
    loading: isActivitiesLoading,
    perPage: activitiesPerPage
  } = useActivitiesInfiniteQuery({
    startDate: widgetForm?.values?.range?.startDate,
    endDate: widgetForm?.values?.range?.endDate,
    guests: widgetForm?.values?.numberOfGuests || '',
    ownerId
  }, !!productId);

  const {
    rentalsPage,
    rentalItems,
    fetchRentalItemsData,
    loading: isRentalLoading,
    perPage: rentalItemsPerPage
  } = useRentalItemsInfiniteQuery({
    startDate: widgetForm?.values?.range?.startDate,
    endDate: widgetForm?.values?.range?.endDate,
    units: widgetForm?.values?.numberOfUnits || '',
    ownerId
  }, !!productId);

  const isActivitiesAccommodationsLoading = isAccommodationsLoading || isActivitiesLoading || isRentalLoading;

  const isAccommodationBooking =
    !isActivitiesAccommodationsLoading &&
    ((!!accommodations?.length && productsMenuIndex === 0) || !!receivedAccommodationId);
  
  const isRentalBooking =
  !isActivitiesAccommodationsLoading &&
  ((!!rentalItems?.length && productsMenuIndex === 2) || !!receivedRentalId);


  const { loading: accommodationLoading, data: accommodationData} = useQuery(
    ACCOMMODATION,
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: isProductsList || selectedProduct || !isAccommodationBooking || receivedActivityId,
      variables: {
        accommodationId: productId,
        businessOwnerId: ownerId
      }
    }
  );

  const { loading: activityLoading, data: activityData } = useQuery(ACTIVITY, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: isProductsList || selectedProduct || isAccommodationBooking || receivedAccommodationId,
    variables: {
      activityId: productId,
      businessOwnerId: ownerId
    }
  });

  const { loading: rentalLoading, data: rentalData } = useQuery(RENTAL_ITEM, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: isProductsList || selectedProduct || isAccommodationBooking || receivedAccommodationId,
    variables: {
      rentalItemsId: productId,
      businessOwnerId: ownerId
    }
  });
  

  useEffect(() => {
    if (!settingsData) return;

    if (!settingsData.payPalSettings && !settingsData.stripeSettings) {
      const payOnArrivalPossibility = settingsData.payOnArrivalPossibility;
      if (payOnArrivalPossibility && payOnArrivalPossibility.payOnArrival) {
        return;
      }
      confirm({
        title: 'Payments were not added',
        confirmationText: 'Okay'
      })
        .then(() => {
          resetWidgetForm();
          ReactDOM.unmountComponentAtNode(document.getElementById(WIDGET_ROOT));
          window.parent.document.getElementById(
            WIDGET_WRAPPER_ID
          ).style.display = 'none';
        })
        .catch(() => {});
    }
  }, [settingsData]);

  useEffect(() => {
    if (!accommodationData || !accommodationData.accommodations.length) return;

    const receivedAccommodation = accommodationData.accommodations[0];    
    const data = getReceivedAccommodationData(receivedAccommodation);

    const firstPrice = receivedAccommodation.prices[0];
    // const futurePrice = receivedAccommodation ?.futurePrices[0];
    
    // Determine base prices type from the available raw data
    let basePricesType = FIXED_PRICES;

    if (firstPrice?.participant?.participant) {
      basePricesType = CATEGORY_PRICES;
    } else if (firstPrice?.day) {
      basePricesType = DAILY_PRICES;
    }

    data['basePricesType'] = basePricesType;

    if (receivedAccommodation?.futurePrices?.[0]?.ratePlanPrices) {
      data['pricesType'] = FUTURE_PRICES;
    } else {
      data['pricesType'] = basePricesType;
    }


    const listOfAvailableCurrencies = AVAILABLE_CURRENCIES;
    if (!listOfAvailableCurrencies.includes(initialSelectedCurrency)) {
      setInitialSelectedCurrency(listOfAvailableCurrencies[0]);
    }

    unstable_batchedUpdates(() => {
      setReceivedAccommodation(receivedAccommodation);
      setAccommodation(data);
      setDisabledStep(getDisabledSteps(data));
      setInitialAvailableCurrencies(listOfAvailableCurrencies);
    })
  }, [accommodationData]);

  useEffect(() => {
    if (!activityData || !activityData.activities.length) return;

    const receivedActivity = activityData.activities[0];

    setActivityDisabledDates(
      receivedActivity.activitybookingSet
        .filter(({ isCanceled }) => !isCanceled)
        .map(({ startingDate, endingDate }) => {
          return getRange(startingDate, endingDate).map(date =>
            date.format('YYYY-MM-DD')
          );
        })
        .flat(1)
    );

    const data = getReceivedActivityData(receivedActivity);
      if (receivedActivity?.futurePrices?.length > 0) {
      const hasValidFuturePrices = receivedActivity.futurePrices.some(
        fp => fp.ratePlanPrices && fp.ratePlanPrices.length > 0
      );
      
      if (hasValidFuturePrices) {
        data['futurePriceForm'] = receivedActivity['futurePriceForm'];
        
        data['pricesType'] = receivedActivity['pricesType']; 
      }
    } else {

      data['pricesType'] = receivedActivity['pricesType'];
    }
       data['pricesType'] = receivedActivity['pricesType'];
       data['scheduleType'] = receivedActivity['scheduleType'];
    

    const listOfAvailableCurrencies = AVAILABLE_CURRENCIES;
    
    if (!listOfAvailableCurrencies.includes(initialSelectedCurrency)) {
      setInitialSelectedCurrency(listOfAvailableCurrencies[0]);
    }

    unstable_batchedUpdates(() => {
      setReceivedActivity(receivedActivity);
      setActivity(data);
      setDisabledStep(getDisabledSteps(data));
      setInitialAvailableCurrencies(listOfAvailableCurrencies);
    });
  }, [activityData]);


   useEffect(() => {
    if (!rentalData || !rentalData.rentalItems.length) return;

    const receivedRental = rentalData.rentalItems[0];


    const data = getReceivedRentalItemsData(receivedRental);
    
       data['pricesType'] = receivedRental['pricesType'];
      if (receivedRental.masterMinMaxHours) {
        data['minMaxHours'] = receivedRental.masterMinMaxHours;
      }
      data['pricesType'] = receivedRental['pricesType'];
      data['minDays'] = receivedRental.minDays;
      data['maxDays'] = receivedRental.maxDays;

    
    const listOfAvailableCurrencies = AVAILABLE_CURRENCIES;
    
    if (!listOfAvailableCurrencies.includes(initialSelectedCurrency)) {
      setInitialSelectedCurrency(listOfAvailableCurrencies[0]);
    }

    unstable_batchedUpdates(() => {
      setReceivedRental(receivedRental);
      setRentalItem(data);
      setDisabledStep(getDisabledSteps(data));
      setInitialAvailableCurrencies(listOfAvailableCurrencies);
    });
  }, [rentalData])

  const onBackHandler = () => {
    if (selectedProduct) {
      unstable_batchedUpdates(() => {
        setInitialAvailableCurrencies(AVAILABLE_CURRENCIES);
        setSelectedProduct(null);
        setIsProductsList(true);
      });
      changeWidgetFormFieldValue('range', { startDate: null, endDate: null });
      changeWidgetFormFieldValue('numberOfGuests', 0);
      return;
    }

    if (menuIndex) {
      const newMenuIndex = getNextStepIncludeDisabled(menuIndex - 1, true);
      setMenuIndex(newMenuIndex);
      return;
    }

    cancelWidgetBooking();
  };

  const cancelWidgetBooking = () => {
    confirm({
      title: 'You have unsaved changes',
      description: t('Are you sure you want to cancel?'),
      confirmationText: 'Cancel',
      cancellationText: 'Back'
    })
      .then(() => {
        ReactGA.event({
          category: 'widget',
          action: `Cancel booking from widget`,
          label: 'widget'
        });
        resetWidgetForm();
        ReactDOM.unmountComponentAtNode(document.getElementById(WIDGET_ROOT));
        window.parent.document.getElementById(WIDGET_WRAPPER_ID).style.display =
          'none';
      })
      .catch(() => {});
  };

  const setMenuIndexHandler = index => {
    const CHECKOUT_SECTION = 3;
    if (
      index > lastSubmittedFormIndex ||
      (index > menuIndex && widgetForm.syncErrors)
    ) {
      enqueueSnackbar(
        t(
          'You should complete and save all previous steps before you can go to this step'
        ),
        {
          variant: 'info',
          autoHideDuration: 5000
        }
      );
      if (widgetForm.syncErrors && index !== CHECKOUT_SECTION) {
        remoteSubmitForm();
      }
      return;
    }

    if (index < menuIndex) {
      updateFormErrors();
    }

    setMenuIndex(index);
    ReactGA.event({
      category: 'widget',
      action: `go to ${getNextTabName(index)} from ${getNextTabName(
        menuIndex
      )}`,
      label: 'widget'
    });
  };

  const getConfirmButtonText = () => {
    if (selectedProduct) return 'Book';

    const PAYMENT_SECTION_INDEX = 4;
    return menuIndex === PAYMENT_SECTION_INDEX ? 'Confirm payment' : 'Next';
  };

  const getCancelButtonText = () => {
    if (selectedProduct || menuIndex) return 'Back';
    return 'Cancel';
  };

  const getNextStepIncludeDisabled = (newMenuIndex, isBack) => {
    const firstDisabledIndex = isBack ? RELATED_PRODUCTS_INDEX : ADD_ONS_INDEX;
    const secondDisabledIndex = isBack ? ADD_ONS_INDEX : RELATED_PRODUCTS_INDEX;

    if (disabledSteps.length) {
      if (
        newMenuIndex === firstDisabledIndex &&
        disabledSteps.includes(firstDisabledIndex)
      ) {
        newMenuIndex = isBack ? newMenuIndex - 1 : newMenuIndex + 1;
      }
      if (
        newMenuIndex === secondDisabledIndex &&
        disabledSteps.includes(secondDisabledIndex)
      ) {
        newMenuIndex = isBack ? newMenuIndex - 1 : newMenuIndex + 1;
      }
    }

    return newMenuIndex;
  };

  const getNextTabName = tabIndex => {
    switch (tabIndex) {
      case PRODUCT_SECTION_INDEX:
        return 'product section';
      case ADD_ONS_SECTION_INDEX:
        return 'add-ons section';
      case RELATED_PRODUCT_SECTION_INDEX:
        return 'related product section';
      case CHECKOUT_SECTION_INDEX:
        return 'checkout section';
      case PAYMENT_SECTION_INDEX:
        return 'payment section';
    }
  };

  const nextStep = () => {
    let newMenuIndex = getNextStepIncludeDisabled(menuIndex + 1);

    if (newMenuIndex > lastSubmittedFormIndex) {
      setLastSubmittedFormIndex(newMenuIndex);
    }
    setMenuIndex(newMenuIndex);

    ReactGA.event({
      category: 'widget',
      action: `go to ${getNextTabName(newMenuIndex)}`,
      label: 'widget'
    });
  };

  const remoteSubmitForm = () => {
    if (selectedProduct) {
      unstable_batchedUpdates(() => {
        setProductId(selectedProduct.id);
        setSelectedProduct(null);
        setInitialAvailableCurrencies([]);
      });
      return;
    }

    submitForm();
  };

  const selectProduct = item => {
    const listOfAvailableCurrencies = item.listOfAvailableCurrencies;

    if (!listOfAvailableCurrencies.includes(initialSelectedCurrency)) {
      setInitialSelectedCurrency(listOfAvailableCurrencies[0]);
    }

    if (!item.scheduleType) { // isAccommodation check just used for display the prices in the ProductInfo
        const firstPrice = item.prices ? item.prices[0] : null;
        let basePricesType = FIXED_PRICES;
        if (firstPrice?.participant?.participant) {
            basePricesType = CATEGORY_PRICES;
        } else if (firstPrice?.day) {
            basePricesType = DAILY_PRICES;
        }
        item.basePricesType = basePricesType;
       
        if (!item.futurePrices || !item.futurePrices.length || !item.futurePrices[0].ratePlanPrices) {
            item.pricesType = basePricesType;
        }
    }

    unstable_batchedUpdates(() => {
      setInitialAvailableCurrencies(listOfAvailableCurrencies);
      setSelectedProduct(item);
      setIsProductsList(false);
    });
  };

  const getDisabledSteps = data => {
    const disabledSteps = [];

    if (!data) return [];

    const addOnsLength = data.addOnsForm.addOns.length;
    const relatedActivitiesLength = data.relatedActivitiesForm
      ? data.relatedActivitiesForm.relatedActivities.length
      : 0;
    const relatedAccommodationsLength = data.relatedAccommodationsForm
      ? data.relatedAccommodationsForm.relatedAccommodations.length
      : 0;

    if (!addOnsLength) disabledSteps.push(ADD_ONS_INDEX);
    if (!relatedActivitiesLength && !relatedAccommodationsLength) {
      disabledSteps.push(RELATED_PRODUCTS_INDEX);
    }

    return disabledSteps;
  };

  const availableSections = useMemo(() => {
    const result = [];

    if (isActivitiesAccommodationsLoading && !accommodations && !activities && !rentalItems) {
      return result;
    }

    if (accommodations?.length) {
      result.push(ACCOMMODATION_TITLE);
    }

    if (activities?.length) {
      result.push(ACTIVITY_TITLE);
    }
    if (rentalItems?.length) {
      result.push(RENTAL_ITEM_TITLE);
    }

    return result;
  }, [isActivitiesAccommodationsLoading, accommodations, activities,rentalItems])

  const loading = settingsLoading || accommodationLoading || activityLoading || isActivitiesAccommodationsLoading;

  useEffect(() => {
    const handleZoomChange = () => {
      const currentZoom = window.devicePixelRatio;
      if (currentZoom !== zoomLevel) {
        setZoomLevel(currentZoom);

        const timeout = setTimeout(() => {
          document.body.style.zoom = "100%"; 
          setZoomLevel(window.devicePixelRatio); 
        }, 6000);

        return () => clearTimeout(timeout); 
      }
    };

    window.addEventListener("resize", handleZoomChange);

    return () => {
      window.removeEventListener("resize", handleZoomChange);
    };
  }, [zoomLevel]);
  
  return (
    <div className={classes.wrapper}>
      <WidgetLoader loading={loading || isSubmit} />

      <Header
        cancelCallback={cancelWidgetBooking}
        title={
          loading
            ? ""
            : isAccommodationBooking
            ? "Book your accommodation"
            : isRentalBooking
            ? "Book your rental Item"
            : "Book your activity"
        }
        initialAvailableCurrencies={initialAvailableCurrencies}
        initialSelectedCurrency={initialSelectedCurrency}
        setInitialSelectedCurrency={setInitialSelectedCurrency}
        initialSelectedLanguage={initialSelectedLanguage}
        setInitialSelectedLanguage={setInitialSelectedLanguage}
        initialAvailableLanguages={languages}
        logo={logo}
      />

      {!!productId && (
        <Menu
          items={receivedRental ? MENU_RENTAL : MENU}
          disabledSteps={disabledSteps}
          isAccommodationBooking={isAccommodationBooking}
          isRentalBooking={isRentalBooking}
          currentIndex={menuIndex}
          setIndex={setMenuIndexHandler}
          lastSubmittedFormIndex={lastSubmittedFormIndex}
        />
      )}

      {isProductsList && !!availableSections.length && (
        <Menu
          isFilter={!!availableSections.length}
          isProductsMenu={true}
          items={availableSections.map(section => ({ title: section }))}
          currentIndex={productsMenuIndex}
          setIndex={setProductsMenuIndex}
          lastSubmittedFormIndex={lastSubmittedFormIndex}
          isAccommodationBooking={isAccommodationBooking}
          isRentalBooking={isRentalBooking}
        />
      )}

      {isProductsList && !!availableSections.length && (
        <>
          {availableSections.includes(ACCOMMODATION_TITLE) && (
            <TabPanel
              value={productsMenuIndex}
              index={0}
              className={classes.tabPanel}
            >
              <GeneralAccommodationsList
                initialSelectedCurrency={initialSelectedCurrency}
                accommodations={accommodations}
                page={accommodationsPage}
                selectProduct={selectProduct}
                fetchData={fetchAccommodationsData}
                perPage={accommodationsPerPage}
              />
            </TabPanel>
          )}
          {availableSections.includes(ACTIVITY_TITLE) && (
            <TabPanel
              value={productsMenuIndex}
              index={availableSections.includes(ACCOMMODATION_TITLE) ? 1 : 0}
              className={classes.tabPanel}
            >
              <GeneralActivitiesList
                initialSelectedCurrency={initialSelectedCurrency}
                activities={activities}
                page={activitiesPage}
                selectProduct={selectProduct}
                fetchData={fetchActivitiesData}
                perPage={activitiesPerPage}
              />
            </TabPanel>
          )}

          {availableSections.includes(RENTAL_ITEM_TITLE) && (
            <TabPanel
              value={productsMenuIndex}
              index={2}
              className={classes.tabPanel}
            >
              <GeneralRentalItemsList
                initialSelectedCurrency={initialSelectedCurrency}
                rentalItems={rentalItems}
                page={rentalsPage}
                selectProduct={selectProduct}
                fetchData={fetchRentalItemsData}
                perPage={rentalItemsPerPage}
              />
            </TabPanel>
          )}
        </>
      )}

      {!isActivitiesAccommodationsLoading && !availableSections.length && !productId && (
        <div className={classes.noItemMsg}>
          <h1>No items</h1>
        </div>
      )}

      {selectedProduct && (
        <ProductInfo
          product={selectedProduct}
          initialSelectedCurrency={initialSelectedCurrency}
        />
      )}

      {(accommodation || activity) && (
        <>
          <TabPanel value={menuIndex} index={0} className={classes.tabPanel}>
            {menuIndex === 0 && (
              <DateAndGuests
                ownerId={ownerId}
                isAccommodationBooking={isAccommodationBooking}
                initialSelectedCurrency={initialSelectedCurrency}
                initialSelectedLanguage={initialSelectedLanguage}
                activityDisabledDates={activityDisabledDates}
                onSubmit={nextStep}
                productData={accommodation || activity}
                receivedProduct={receivedAccommodation || receivedActivity}
                languages={languages}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={1} className={classes.tabPanel}>
            {menuIndex === 1 && (
              <AddOns onSubmit={nextStep} accommodationData={accommodation || activity} />
            )}
          </TabPanel>
          
          {accommodation && (
              <MandatoryFees accommodationData={accommodation} />
           )}
          
          <TabPanel value={menuIndex} index={2} className={classes.tabPanel}>
            {menuIndex === 2 && (
              <Products
                logo={logo}
                isAccommodationBooking={isAccommodationBooking}
                onSubmit={nextStep}
                productData={accommodation || activity}
                ownerId={ownerId}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={3} className={classes.tabPanel}>
            {menuIndex === 3 && (
              <Checkout
                isAccommodationBooking={isAccommodationBooking}
                onSubmit={nextStep}
                productData={accommodation || activity}
                title="Booking summary"
                productsMenuIndex={productsMenuIndex}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={4} className={classes.tabPanel}>
            {menuIndex === 4 && (
              <Payment
                isAccommodationBooking={isAccommodationBooking}
                onSubmit={() => {
                  setIsSubmit(true);
                  setLastSubmittedFormIndex(menuIndex + 1);
                  ReactGA.event({
                    category: 'widget',
                    action: `submit booking from widget`,
                    label: 'widget'
                  });
                }}
                setIsSubmit={setIsSubmit}
                isSubmit={isSubmit}
                productData={accommodation || activity}
                settingsData={settingsData}
              />
            )}
          </TabPanel>
        </>
      )}

      {rentalItem && (
        <>
          <TabPanel value={menuIndex} index={0} className={classes.tabPanel}>
            {menuIndex === 0 && (
              <DatesAndUnits
                ownerId={ownerId}
                initialSelectedCurrency={initialSelectedCurrency}
                initialSelectedLanguage={initialSelectedLanguage}
                onSubmit={nextStep}
                productData={rentalItem}
                receivedProduct={receivedRental}
                languages={languages}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={1} className={classes.tabPanel}>
            {menuIndex === 1 && (
              <AddOns onSubmit={nextStep} accommodationData={rentalItem} />
            )}
          </TabPanel>

      
          <TabPanel value={menuIndex} index={3} className={classes.tabPanel}>
            {menuIndex === 3 && (
              <RentalCheckout  
                onSubmit={nextStep}
                productData={rentalItem}
                title="Booking summary"
                productsMenuIndex={productsMenuIndex}
              />
            )}
          </TabPanel>
          <TabPanel value={menuIndex} index={4} className={classes.tabPanel}>
            {menuIndex === 4 && (
              <Payment
               isRentalBooking={isRentalBooking}
                onSubmit={() => {
                  setIsSubmit(true);
                  setLastSubmittedFormIndex(menuIndex + 1);
                  ReactGA.event({
                    category: 'widget',
                    action: `submit booking from widget`,
                    label: 'widget'
                  });
                }}
                setIsSubmit={setIsSubmit}
                isSubmit={isSubmit}
                productData={rentalItem}
                settingsData={settingsData}
                rentalData={rentalData}
              />
            )}
          </TabPanel>
        </>
      )}

      {!isProductsList && (
        <Footer
          menuIndex={menuIndex}
          isAccommodationBooking={isAccommodationBooking}
          isSelectedProduct={!!selectedProduct}
          cancelButton={getCancelButtonText()}
          confirmButton={getConfirmButtonText()}
          confirmCallback={remoteSubmitForm}
          cancelCallback={onBackHandler}
          accommodationData={accommodation || activity}
          rentalData={rentalItem}
          isHideConfirmButton={isProductsList}
          isLastStep={menuIndex === 4}
          privacyPolicy={privacyPolicy}
          termsAndConditions={termsAndConditions}
          ownerId={ownerId}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    widgetForm: state.form.widgetForm
  };
};

const mapDispatchToProps = dispatch => ({
  changeWidgetFormFieldValue: (field, value) => {
    dispatch(change('widgetForm', field, value));
  },
  submitForm: () => dispatch(submit('widgetForm')),
  updateFormErrors: () => dispatch(updateSyncErrors('widgetForm', {})),
  resetWidgetForm: () => dispatch(reset('widgetForm'))
});

export default connect(mapStateToProps, mapDispatchToProps)(Widget);
